import { useState } from "react";
import { BAY_ISLE, TERRACOTTA, SEMI_SWEET, PROPER_GREY, RULE, WHITE, PLASTER, FONT_DISPLAY, FONT_DISPLAY_HEAVY, FONT_SERIF, FONT_BODY } from "../theme";

function encode(data) {
  return Object.entries(data).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
}

function ChoiceCard({ label, sub, icon, selected, onClick }) {
  return (
    <div onClick={onClick} style={{
      border: selected ? `2px solid ${TERRACOTTA}` : `1px solid ${RULE}`,
      borderRadius: 14, padding: "22px 20px",
      background: selected ? "rgba(195,135,98,0.05)" : WHITE,
      cursor: "pointer", transition: "all 0.16s", userSelect: "none",
      display: "flex", alignItems: "flex-start", gap: 14,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        background: selected ? "rgba(195,135,98,0.12)" : "rgba(172,167,166,0.1)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <i className={`ti ${icon}`} aria-hidden="true" style={{ fontSize: 20, color: selected ? TERRACOTTA : PROPER_GREY }} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: BAY_ISLE, marginBottom: 4, fontFamily: FONT_DISPLAY }}>{label}</p>
        {sub && <p style={{ fontSize: 12, color: PROPER_GREY, lineHeight: 1.5, fontFamily: FONT_BODY }}>{sub}</p>}
      </div>
      <div style={{
        width: 20, height: 20, borderRadius: "50%", flexShrink: 0, marginTop: 2,
        border: selected ? `6px solid ${TERRACOTTA}` : `1.5px solid ${PROPER_GREY}`,
        background: WHITE, transition: "all 0.15s",
      }} />
    </div>
  );
}

function PillChoice({ label, selected, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: "9px 18px", borderRadius: 999, cursor: "pointer", fontFamily: FONT_BODY,
      fontSize: 12.5, fontWeight: 600, transition: "all 0.15s",
      border: selected ? `2px solid ${TERRACOTTA}` : `1px solid ${RULE}`,
      background: selected ? "rgba(195,135,98,0.07)" : WHITE,
      color: selected ? TERRACOTTA : SEMI_SWEET,
    }}>
      {label}
    </button>
  );
}

function StepLabel({ num, text }) {
  return (
    <p style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase",
      color: PROPER_GREY, marginBottom: 14, fontFamily: FONT_BODY,
      display: "flex", alignItems: "center", gap: 8,
    }}>
      <span style={{
        width: 20, height: 20, borderRadius: "50%", background: BAY_ISLE, color: TERRACOTTA,
        fontSize: 10, fontFamily: FONT_DISPLAY, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>{num}</span>
      {text}
    </p>
  );
}

const AGENT_INTERESTS = ["AI Listing Boost", "Custom project", "Both", "Not sure yet"];
const BIZ_INTERESTS   = ["Content support", "Done-for-you systems", "Both", "Not sure yet"];
const MARKETING_SETUPS = ["None — starting fresh", "DIY right now", "Working with another agency", "Not sure"];
const TIMELINES = ["ASAP", "Next 1–3 months", "Just exploring"];
const BUDGETS   = ["Under $200", "$200–$500/mo", "$500–$1,000/mo", "$1,000+/mo", "Prefer not to say"];

export default function GetStarted() {
  const [audience, setAudience]     = useState(null);
  const [interest, setInterest]     = useState(null);
  const [marketing, setMarketing]   = useState(null);
  const [timeline, setTimeline]     = useState(null);
  const [budget, setBudget]         = useState(null);

  // Agent-specific
  const [brokerage, setBrokerage]   = useState("");
  const [listings, setListings]     = useState("");

  // Business-specific
  const [bizName, setBizName]       = useState("");
  const [industry, setIndustry]     = useState("");

  // Shared contact
  const [name, setName]             = useState("");
  const [email, setEmail]           = useState("");
  const [phone, setPhone]           = useState("");
  const [notes, setNotes]           = useState("");

  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState("");
  const [loading, setLoading]       = useState(false);

  const isAgent = audience === "agent";
  const isBiz   = audience === "business";
  const interests = isAgent ? AGENT_INTERESTS : BIZ_INTERESTS;
  const showStep3 = audience && interest && marketing;
  const showStep4 = showStep3 && timeline;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!audience)  { setError("Please tell us who you are to continue."); return; }
    if (!interest)  { setError("Please select what you're interested in."); return; }
    if (!marketing) { setError("Please tell us about your current marketing setup."); return; }
    if (!timeline)  { setError("Please select a timeline."); return; }
    if (!name.trim() || !email.trim()) { setError("Please fill in your name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Please enter a valid email address."); return; }
    setError(""); setLoading(true);
    try {
      const payload = {
        "form-name": "acadia-get-started", "bot-field": "",
        audience_type: audience,
        interested_in: interest,
        current_marketing: marketing,
        timeline,
        budget: budget || "Not specified",
        full_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        notes: notes.trim(),
      };
      if (isAgent) {
        payload.brokerage = brokerage.trim();
        payload.listings_per_month = listings.trim();
      }
      if (isBiz) {
        payload.business_name = bizName.trim();
        payload.industry = industry.trim();
      }
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode(payload),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again or email us at inquiries@acadiamediamarketing.com.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    const first = name.trim().split(" ")[0];
    return (
      <div style={{ maxWidth: 560, margin: "0 auto", padding: "5rem 20px", textAlign: "center" }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", background: "rgba(195,135,98,0.1)",
          display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem",
        }}>
          <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
            <path d="M4 13l5.5 5.5L22 7" stroke={TERRACOTTA} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h1 style={{ fontFamily: FONT_SERIF, fontStyle: "italic", fontSize: "clamp(26px, 5vw, 36px)", color: BAY_ISLE, marginBottom: 12, fontWeight: 400 }}>
          You're all set{first ? `, ${first}` : ""}!
        </h1>
        <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.75, maxWidth: 400, margin: "0 auto 24px", fontFamily: FONT_BODY }}>
          We got your request and will be in touch within 1–2 business days to talk through next steps.
        </p>
        <p style={{ fontSize: 12, color: PROPER_GREY, fontFamily: FONT_BODY }}>
          Questions in the meantime? Email us at{" "}
          <a href="mailto:inquiries@acadiamediamarketing.com" style={{ color: TERRACOTTA }}>
            inquiries@acadiamediamarketing.com
          </a>
        </p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 660, margin: "0 auto", padding: "0 20px 5rem" }}>

      {/* HEADER */}
      <div style={{ textAlign: "center", padding: "3rem 0 2.5rem" }}>
        <p style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase",
          color: TERRACOTTA, borderBottom: `1.5px solid ${TERRACOTTA}`,
          display: "inline-block", paddingBottom: 4, marginBottom: 18, fontFamily: FONT_BODY,
        }}>
          Let's work together
        </p>
        <h1 style={{ fontFamily: FONT_DISPLAY_HEAVY, fontSize: "clamp(26px, 5.5vw, 40px)", color: BAY_ISLE, lineHeight: 1.12, marginBottom: 14, fontWeight: 400 }}>
          Get started with{" "}
          <em style={{ color: TERRACOTTA, fontStyle: "italic", fontFamily: FONT_SERIF, fontSize: "1.1em" }}>Acadia</em>
        </h1>
        <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.75, maxWidth: 430, margin: "0 auto", fontFamily: FONT_BODY }}>
          Tell us a little about your business and what you're looking for. We'll reach out within 1–2 business days.
        </p>
      </div>

      <form name="acadia-get-started" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit} noValidate>
        <input type="hidden" name="bot-field" />
        <input type="hidden" name="form-name" value="acadia-get-started" />

        {/* STEP 1 — WHO ARE YOU */}
        <div style={{ marginBottom: "1.75rem" }}>
          <StepLabel num="1" text="Who are you?" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <ChoiceCard
              label="Real Estate Agent"
              sub="I sell homes and want AI-powered listing marketing."
              icon="ti-home"
              selected={audience === "agent"}
              onClick={() => { setAudience("agent"); setInterest(null); }}
            />
            <ChoiceCard
              label="Small Business Owner"
              sub="I run a local business and need content and marketing support."
              icon="ti-building-store"
              selected={audience === "business"}
              onClick={() => { setAudience("business"); setInterest(null); }}
            />
          </div>
        </div>

        {/* STEP 2 — AUDIENCE-SPECIFIC FIELDS */}
        {audience && (
          <div style={{ marginBottom: "1.75rem" }}>
            <StepLabel num="2" text={isAgent ? "Tell us about your business" : "Tell us about your business"} />
            <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 12, padding: "20px 18px" }}>

              {isAgent && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label>Brokerage or team name <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                    <input type="text" name="brokerage" placeholder="Keller Williams, Compass, RE/MAX..." value={brokerage} onChange={e => setBrokerage(e.target.value)} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label>Approx. active listings per month <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                    <input type="text" name="listings_per_month" placeholder="e.g. 1–2, 3–5, 10+" value={listings} onChange={e => setListings(e.target.value)} />
                  </div>
                </>
              )}

              {isBiz && (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label>Business name <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                    <input type="text" name="business_name" placeholder="Your business name" value={bizName} onChange={e => setBizName(e.target.value)} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label>Industry <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                    <input type="text" name="industry" placeholder="Salon, restaurant, retail, contractor..." value={industry} onChange={e => setIndustry(e.target.value)} />
                  </div>
                </>
              )}

              <div style={{ marginBottom: 16 }}>
                <label style={{ marginBottom: 10, display: "block" }}>I'm interested in</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {interests.map(opt => (
                    <PillChoice key={opt} label={opt} selected={interest === opt} onClick={() => setInterest(opt)} />
                  ))}
                </div>
              </div>

              <div>
                <label style={{ marginBottom: 10, display: "block" }}>Current marketing setup</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {MARKETING_SETUPS.map(opt => (
                    <PillChoice key={opt} label={opt} selected={marketing === opt} onClick={() => setMarketing(opt)} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — TIMELINE + BUDGET */}
        {showStep3 && (
          <div style={{ marginBottom: "1.75rem" }}>
            <StepLabel num="3" text="Timeline & budget" />
            <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 12, padding: "20px 18px" }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ marginBottom: 10, display: "block" }}>When are you looking to get started?</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {TIMELINES.map(opt => (
                    <PillChoice key={opt} label={opt} selected={timeline === opt} onClick={() => setTimeline(opt)} />
                  ))}
                </div>
              </div>
              <div>
                <label style={{ marginBottom: 10, display: "block" }}>Monthly budget range <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {BUDGETS.map(opt => (
                    <PillChoice key={opt} label={opt} selected={budget === opt} onClick={() => setBudget(opt)} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4 — CONTACT */}
        {showStep4 && (
          <div style={{ marginBottom: "1.75rem" }}>
            <StepLabel num="4" text="Your contact info" />
            <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 12, padding: "20px 18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label>Full name *</label>
                  <input type="text" name="full_name" placeholder="Jane Smith" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
                </div>
                <div>
                  <label>Email address *</label>
                  <input type="email" name="email" placeholder="jane@email.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Phone <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                <input type="tel" name="phone" placeholder="(555) 555-5555" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" />
              </div>
              <div>
                <label>Anything else you'd like us to know? <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                <textarea
                  name="notes"
                  placeholder="Upcoming listings, specific goals, questions, anything..."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          </div>
        )}

        {error && (
          <p style={{ fontSize: 12, color: "#7a2a2a", background: "rgba(180,60,60,0.06)", border: "1px solid rgba(180,60,60,0.15)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontFamily: FONT_BODY }}>
            {error}
          </p>
        )}

        {showStep4 && (
          <>
            <button type="submit" disabled={loading} style={{ width: "100%" }}>
              {loading ? "Sending..." : "Submit request →"}
            </button>
            <p style={{ fontSize: 11, color: PROPER_GREY, textAlign: "center", marginTop: 9, lineHeight: 1.5, fontFamily: FONT_BODY }}>
              No commitment required. We'll reach out within 1–2 business days.
            </p>
          </>
        )}
      </form>
    </div>
  );
}
